import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import type { YoutubeSettingsPublic } from "@tih/shared";
import {
  ApiError,
  adminFetchSettingsYoutube,
  adminPutSettingsYoutube,
  adminTestSettingsYoutube,
} from "../../../api/client";

const EMPTY: YoutubeSettingsPublic = {
  apiKeySet: false,
  configured: false,
  encryptionAvailable: false,
  channelId: null,
  channelTitle: null,
  lastSyncAt: null,
  videoCount: 0,
};

export function AdminSettingsYoutubePage() {
  const [form, setForm] = useState(EMPTY);
  const [apiKey, setApiKey] = useState("");
  const [channelId, setChannelId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await adminFetchSettingsYoutube();
      setForm(data);
      setApiKey("");
      setChannelId(data.channelId ?? "");
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await adminPutSettingsYoutube({
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
        channelId: channelId.trim(),
      });
      setForm(data);
      setApiKey("");
      setChannelId(data.channelId ?? "");
      setSuccess("YouTube settings saved.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  async function testKey() {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await adminTestSettingsYoutube();
      setSuccess(`API key works — sample video: “${data.channelTitle}”.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Test failed");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div style={{ opacity: 0.5 }}>Loading…</div>;

  return (
    <div>
      <section className="admin-card">
        <h2 className="admin-card-title">YouTube Data API</h2>
        <p className="admin-card-sub">
          Powers live/replay <strong>view counts</strong> on the public site (replacing simulated
          numbers), the <Link to="/admin/youtube">YouTube library</Link>, video stats on the admin
          dashboard, and optional comment sync on{" "}
          <a href="/admin/comments">Live comments</a>. Enable{" "}
          <a href="https://console.cloud.google.com/apis/library/youtube.googleapis.com" target="_blank" rel="noreferrer">
            YouTube Data API v3
          </a>{" "}
          in Google Cloud and create an API key.
        </p>

        {form.configured ? (
          <p className="admin-card-foot" style={{ marginBottom: 16 }}>
            Status: <strong>Configured</strong>
            {form.apiKeySet ? " (stored in database)" : " (from server env)"}
            {form.videoCount > 0 && <> · {form.videoCount} videos in library</>}
          </p>
        ) : (
          <p className="admin-card-foot" style={{ marginBottom: 16, color: "var(--accent)" }}>
            Not configured — public stats will use simulated viewer numbers until a key is set.
          </p>
        )}

        <form onSubmit={save} className="admin-content-form">
          <label className="admin-field">
            <span>API key {form.apiKeySet ? "(leave blank to keep current)" : ""}</span>
            <input
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              disabled={busy}
              placeholder={form.apiKeySet ? "••••••••" : "AIza…"}
            />
          </label>
          <label className="admin-field">
            <span>Channel id or @handle</span>
            <input
              type="text"
              autoComplete="off"
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              disabled={busy}
              placeholder="@YourChannel or UC…"
            />
          </label>
          {form.channelTitle && (
            <p className="admin-card-foot">
              Resolved channel: <strong>{form.channelTitle}</strong>
              {form.lastSyncAt && (
                <> · last synced {new Date(form.lastSyncAt).toLocaleString()}</>
              )}
            </p>
          )}
          {!form.encryptionAvailable && (
            <p className="admin-card-sub">
              Set <code>SETTINGS_ENCRYPTION_KEY</code> on the server to save a key here, or use{" "}
              <code>YOUTUBE_API_KEY</code> in the deployment environment.
            </p>
          )}
          <div className="admin-blog-form-actions" style={{ justifyContent: "flex-start" }}>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy || !form.configured}
              onClick={() => void testKey()}
            >
              Test API key
            </button>
            <Link to="/admin/youtube" className="btn btn-ghost">
              Open video library
            </Link>
          </div>
        </form>
      </section>

      {error && <div className="form-error">{error}</div>}
      {success && <div className="form-success" style={{ padding: 12 }}>{success}</div>}
    </div>
  );
}
